"use client";
import { useParams, useSearchParams, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import style from "./Drama.module.css";
import VideoPlayer from "@/app/component/videoplayer/VideoPlayer";
import { FavoriteDrama, useFavoriteStore, useShareModalStore, useToastStore, useUnlockModalStore } from "@/app/store/localapi";
import { useMemo } from "react";
import { favunfav } from "@/app/utils/favunfav";
import { useAuthStore } from "@/app/store/authstore";
import { fetchWithLoader } from "@/app/utils/api";
import { FavoriteIcon, LockIcon, ShareIcon, UnFavoriteIcon } from "@/app/component/videoplayer/PlayerIcons";
import MusicBar from "./MusicBar";
import ImageSlider, { UpComingDrama } from "./ImageSlider";
import UnlockModal from "@/app/component/videoplayer/UnlockModal";
import DisplayAd from '@/app/component/ad/DisplayAd';
import { useCoinStore } from "@/app/store/coinstore";
import { addCoinData } from "@/app/utils/coinApi";
import { useRemoteConfigStore } from "@/app/store/remoteConfigStore";

interface DramaData {
  d_id: string;
  d_name: string;
  d_desc: string;
  d_image: string;
  duration: string;
  epi_id: string;
  epi_name: string;
  type: string;
  added_dt: string;
  thumbnails: string;
  total_d_stream: string;
  unlock_count: string;
  translation: string;
  "240p": string;
  "480p": string;
  "720p": string;
  "nosub720p": string;
  "nosub480p": string;
  "nosub240p": string;
}
interface CaptionData {
  d_id: string;
  episode_name: string;
  subtitles: { lang_code: string; lang_file: string; }[];
}

function Player() {
  const openShareModal = useShareModalStore((state) => state.openShareModal);
  const { showToast } = useToastStore();
  const user = useAuthStore((state) => state.user);
  const router = useRouter();

  // Global modal store for sync with VideoPlayer
  const globalModalState = useUnlockModalStore((state) => state.isOpen);
  const setGlobalModalState = useUnlockModalStore((state) => state.setModalOpen);

  const { dramaSlug } = useParams<{ dramaSlug: string }>();
  const dramaId = dramaSlug?.substring(dramaSlug.lastIndexOf('-') + 1);
  const searchParams = useSearchParams();
  const query: number = Number(searchParams.get('episode') || searchParams.get('ep') || 0);
  const [drama, setDrama] = useState<DramaData[]>([]);
  const [desc, setDesc] = useState("");
  const [readmore, setReadMore] = useState(false);
  const [active, setActive] = useState<DramaData | null>(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(query || 0);
  const [isplaying, setIsplaying] = useState(false);
  const [modal, setModal] = useState(false)
  const [correctDramaSlug, setCorrectDramaSlug] = useState<string | null>(null)

  const [subtitleLanguage, setSubtitleLanguage] = useState<{ lang_code: string, lang_name: string }[]>([]);
  const [caption, setCaption] = useState<CaptionData[]>([]);
  const [vttSubtitles, setVttSubtitles] = useState<{ lang_code: string; lang_file: string; }[]>([]);
  const [dubbingData, setDubbingData] = useState<{ d_id: string; episode_name: string, dubbed: { lang_code: string, audio_file: string } }[]>([]);
  const [dubbingLanguage, setDubbingLanguage] = useState<{ lang_code: string; lang_name: string; }[]>([]);
  const [audioData, setAudioData] = useState<{ lang_code: string, audio_file: string }[]>([]);
  const [isDubbed, setIsDubbed] = useState(false);
  const [upComingData, setUpComingData] = useState<UpComingDrama[]>([]);
  const [recomendedData, setRecomendedData] = useState<UpComingDrama[]>([]);
  const [isUpcoming, setIsUpcoming] = useState(false);


  const favorites = useFavoriteStore((state) => state.favorites);
  const addFavorite = useFavoriteStore((state) => state.addFavorite);
  const removeFavorite = useFavoriteStore((state) => state.removeFavorite);

  // Coin store and config
  const coins = useCoinStore((state) => state.coinBalance);
  const config = useRemoteConfigStore((state) => state.config);

  // Sync local modal state with global state
  useEffect(() => {
    setModal(globalModalState);
  }, [globalModalState]);

  // Sync global modal state when local modal changes
  useEffect(() => {
    setGlobalModalState(modal);
  }, [modal, setGlobalModalState]);

  // Helper to check if episode is unlocked via coins (from coin store episodes field)
  const isUnlockedViaCoinStore = (episodeId: string): boolean => {
    if (!coins.episodes) return false;
    const unlockedIds = coins.episodes.split(',').map(id => id.trim());
    return unlockedIds.includes(episodeId);
  };

  const isFavorite = useMemo(() => {
    if (!drama[0]) return false;
    return !!favorites[drama[0].d_id];
  }, [favorites, drama]);

  const handleToggleFavorite = async () => {
    let type = ""
    if (!drama[0]) return;
    if (isFavorite) {
      removeFavorite(drama[0].d_id);
      type = "unfavourite"
    } else {
      const favDrama: FavoriteDrama = {
        d_id: drama[0].d_id,
        d_image: drama[0].d_image,
        d_title: drama[0].d_name,
        total_ep: drama.length,
        d_desc: drama[0].d_desc,
      };
      addFavorite(favDrama);
      type = "favourite"
    }
    if (!user) return;
    await favunfav({
      d_id: drama[0].d_id,
      user_id: user?.user_id,
      type: type
      // eslint-disable-next-line
    }, (msg: string, type: string) => showToast(msg, type as any))

  };
  // Dedicated guard: redirect to /404 immediately if dramaId is not a valid number
  useEffect(() => {
    if (!dramaId || !/^\d+$/.test(dramaId)) {
      router.replace('/404');
    }
  }, [dramaId, router]);

  // Fetch all data in single call combined
  useEffect(() => {
    const fetchAllData = async () => {
      // Silent guard — redirect is handled by the dedicated effect above
      if (!dramaId || !/^\d+$/.test(dramaId)) return;

      try {
        const allPromises = [
          fetchWithLoader(`drama_epi_list.php?d_id=${dramaId}&currentpage=1`),
          fetchWithLoader("language_subtile.php"),
          fetchWithLoader("subtitle_selection.php", {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ d_id: dramaId })
          }),
          fetchWithLoader(`drama_dubbed.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ d_id: dramaId })
          }),
          fetchWithLoader(`dubbed_language.php`),
        ];
        const [dramaRes, languageRes, subtitleRes, dubbingRes, dubbedLanguageRes] = await Promise.all(allPromises);

        const recomendres = await fetch(`https://dramasstory.com/web/drama_recommend.php?drama_id=${dramaId}`);
        const recomenddata = await recomendres.json();
        if (recomenddata.data?.data) {
          const adaptedData = recomenddata.data.data.map((d: any) => ({
            d_id: d.d_id,
            d_logo: d.d_image,
            d_name: d.d_name,
            description: d.d_desc,
            gradient: d.gradient,
            totl_episodes: '', 
            unlock_count: d.unlock_count,
          }));
          setRecomendedData(adaptedData);
        }


        const languageData = await languageRes?.json();
        const subtitleData = await subtitleRes?.json();
        const dramaData = await dramaRes?.json();
        const dubbingData = await dubbingRes?.json();
        const dubbedLanguageData = await dubbedLanguageRes?.json();

        if (dramaData?.data?.data && dramaData.data.data.length > 0) {
          // Store the correct slug — the unified URL effect below will do the single router.replace
          const actualDramaName: string = dramaData.data.data[0]?.d_name || "";
          if (actualDramaName) {
            const slugified = actualDramaName
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-|-$/g, "");
            setCorrectDramaSlug(`${slugified}-${dramaId}`);
          } else {
            setCorrectDramaSlug(dramaSlug);
          }

          if (dramaData.data?.comingsoon === "0") {
            setIsUpcoming(true);
            setCurrentVideoIndex(0);

            const trailerIndex = dramaData.data.data.findIndex((d: DramaData) => d.epi_name === "Trailer");
            if (trailerIndex !== -1) {
              setDrama([dramaData.data.data[trailerIndex]]);
            }
            try {
              const upcomingRes = await fetch(`https://dramasstory.com/web/coming_soon.php`)
              const upcomingData = await upcomingRes?.json();

              if (upcomingData.data?.Data[0]?.list) {
                setUpComingData(upcomingData.data?.Data[0].list || []);
              } else {
                showToast("Failed to fetch upcoming dramas.", "error");
              }
            } catch (error) {
              console.error("Error fetching upcoming dramas:", error);
              showToast("An error occurred while fetching upcoming dramas.", "error");
            }
          } else {
            setIsUpcoming(false);
            setDrama(dramaData.data?.data);
          }

          setIsDubbed(dramaData.data?.isdubbed === "1");
          setDesc(dramaData.data?.data[0]?.d_desc.slice(0, 150));
        } else {
          // No drama found for this ID → redirect to /404
          router.replace('/404');
        }

        if (languageData.data?.data) {
          setSubtitleLanguage(languageData.data?.data || []);
        } else {
          showToast("Failed to fetch subtitle languages.", "error");
        }

        if (dubbedLanguageData.data?.data) {
          setDubbingLanguage(dubbedLanguageData.data?.data || []);
        } else {
          showToast("Failed to fetch dubbed languages.", "error");
        }
        if (dubbingData.data?.Data) {
          setDubbingData(dubbingData.data?.Data || []);
        } else {
          showToast("Failed to fetch audio tracks.", "error");
        }

        if (subtitleData.data?.Data) {
          setCaption(subtitleData.data?.Data || []);
        } else {
          showToast("Failed to fetch subtitles.", "error");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        // Any fetch failure on an unknown ID should redirect to /404
        router.replace('/404');
      }
    };

    fetchAllData();
  }, [dramaId, showToast]);


  useEffect(() => {
    if (dubbingData.length > 0) {
      const data = dubbingData.find(d => d.episode_name === drama[currentVideoIndex]?.epi_name);
      // @ts-expect-error - data?.dubbed may be undefined or have different type
      setAudioData(data?.dubbed || []);
    }
  }, [dubbingData, currentVideoIndex, drama]);


  // Unified URL effect: fires once after fetch (gated on correctDramaSlug) combining
  // slug correction + episode tracking into a single router.replace — no double navigation
  useEffect(() => {
    if (!dramaId || !/^\d+$/.test(dramaId)) return;
    if (correctDramaSlug === null) return; // wait until fetch resolves the correct slug
    if (isUpcoming && query && query > 0) {
      router.replace(`/drama/${correctDramaSlug}/?episode=0`, { scroll: false });
      return;
    }
    router.replace(`/drama/${correctDramaSlug}/?episode=${currentVideoIndex}`, { scroll: false });
  }, [correctDramaSlug, currentVideoIndex, dramaId, router, isUpcoming, query]);

  // Reset URL effect gate when dramaId changes; drama state is guarded at render time
  useEffect(() => {
    setCorrectDramaSlug(null);
  }, [dramaId]);



  const playvideo = async (d: DramaData, i: number, isFromUrlAccess: boolean = false) => {
    // If episode unlocked or user is premium allow directly
    if (d && d.unlock_count == "0" || (user && user?.premium_data.premium == "1")) {
      setActive(d);
      setCurrentVideoIndex(i);
      if (caption && caption.length > 0) {
        const currentcaption = caption?.find((c: CaptionData) => c.episode_name === d.epi_name);
        if (currentcaption && currentcaption?.subtitles) {
          setVttSubtitles((currentcaption.subtitles) || []);
        }
      }
    } else if (isUnlockedViaCoinStore(d.epi_id)) {
      // Episode is unlocked via coins
      setActive(d);
      setCurrentVideoIndex(i);
      if (caption && caption.length > 0) {
        const currentcaption = caption?.find((c: CaptionData) => c.episode_name === d.epi_name);
        if (currentcaption && currentcaption?.subtitles) {
          setVttSubtitles((currentcaption.subtitles) || []);
        }
      }
    } else {
      // Episode is locked
      if (d) {
        // Only allow coin unlock for logged-in users with sufficient coins
        // All other cases (non-logged-in, insufficient coins, etc.) show premium modal
        if (user && config.unlockCost && config.unlockCost > 0 && coins.totalCoins >= config.unlockCost) {
          // Directly unlock without dialog for logged-in users with enough coins
          try {
            // Call API to unlock episode - send ONLY the new episode ID
            // Backend will concatenate it with existing episodes
            const res = await addCoinData(
              {
                type: "sub",
                episode_ids: d.epi_id,  // Send only the new episode being unlocked
                coins: config.unlockCost
              },
              'episodeUnlock'
            ).catch(() => { /* ignore errors */ });

            if (res && res.ErrorCode === 1) {
              // Update coin store: add new episode to CSV list
              const currentEpisodes = coins.episodes ? coins.episodes.split(',').filter(e => e.trim()) : [];
              if (!currentEpisodes.includes(d.epi_id)) {
                currentEpisodes.push(d.epi_id);
              }
              const updatedEpisodes = currentEpisodes.join(',');

              // Update coin store with new balance and updated episodes CSV
              const newBalance = Math.max(0, coins.totalCoins - config.unlockCost);
              useCoinStore.getState().setCoinBalance({
                ...coins,
                totalCoins: newBalance,
                episodes: updatedEpisodes
              });

              // Play the episode
              setActive(d);
              setCurrentVideoIndex(i);
              if (caption && caption.length > 0) {
                const currentcaption = caption?.find((c: CaptionData) => c.episode_name === d.epi_name);
                if (currentcaption && currentcaption?.subtitles) {
                  setVttSubtitles((currentcaption.subtitles) || []);
                }
              }

              showToast(`Episode unlocked for ${config.unlockCost} coins!`, 'success');
            } else {
              showToast('Failed to unlock episode', 'error');
            }
          } catch (error) {
            console.error('Unlock error:', error);
            showToast('Error unlocking episode', 'error');
          }
        } else {
          // Show premium purchase modal
          setIsplaying(false);
          setActive(drama[0]);
          setCurrentVideoIndex(0);
          setModal(true);
        }
      }
    }
  };


  useEffect(() => {
    if (drama.length > 0) {
      // This is from URL access, so pass true for isFromUrlAccess
      playvideo(drama[query] || drama[currentVideoIndex], query, true);
      if (caption && caption.length > 0) {
        const currentcaption = caption?.find((c: CaptionData) => c.episode_name === drama[currentVideoIndex]?.epi_name);
        if (currentcaption && currentcaption?.subtitles) {
          setVttSubtitles((currentcaption.subtitles) || []);
        }
      }
    }
    // eslint-disable-next-line
  }, [drama, caption]);

  //read more desc
  useEffect(() => {
    const desc = readmore ? drama[0]?.d_desc : drama[0]?.d_desc.slice(0, 150);
    setDesc(desc);
  }, [readmore, drama]);


  const next = () => {
    const nextIndex = (currentVideoIndex + 1) % drama.length;
    playvideo(drama[nextIndex], nextIndex);
  };



  const prev = () => {
    const prevIndex = (currentVideoIndex - 1 + drama.length) % drama.length;
    playvideo(drama[prevIndex], prevIndex);
  };

  const isplay = (status: boolean) => {
    setIsplaying(status);
  };

  // Guard: show loading if drama is empty OR if loaded drama belongs to a different ID
  // (drama[0].d_id check catches stale data synchronously at render time — no useEffect needed)
  if (!drama || drama.length === 0 || drama[0]?.d_id !== dramaId) {
    return <div className={style.container} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '90vh' }}></div>;
  }

  return (
    <>
      <div className={style.container}>
        {/* video player  */}
        <VideoPlayer
          data={active!}
          language={subtitleLanguage}
          subtitles={vttSubtitles}
          nextfn={next}
          prevfn={prev}
          isplay={isplay}
          eindex={currentVideoIndex}
          audioTracks={audioData}
          audioLanguages={dubbingLanguage}
          isDubbed={isDubbed}
          totalEpisodes={drama.length}
        />

        {/* episode list and description */}
        <div className={style.episodebox}>
          <h1 className={style.heading}>{drama[0]?.d_name}</h1>
          {/* share and favorite button */}
          <div className={style.playerBtns}>
            <button type="button" className="commonBtn" onClick={handleToggleFavorite}>
              {!isFavorite ? <FavoriteIcon /> : <UnFavoriteIcon />}
              <span>{isFavorite ? 'Saved' : 'Save'}</span>
            </button>
            <button type="button" className={`${style.playsharebtn} commonBtn`}
              onClick={() => {
                const firstDrama = drama[0];
                if (firstDrama) {
                  openShareModal({
                    d_keywords: "",
                    drama_name: firstDrama.d_name,
                    gradient: "",
                    id: firstDrama.d_id,
                    image_url: firstDrama.d_image,
                    slug: "",
                    status: "",
                    total_episodes: "",
                    unlock_epi_count: "",
                    launch_date: firstDrama.added_dt,
                    added_dt: firstDrama.added_dt,
                    d_desc: firstDrama.d_desc,
                  });
                }
              }}>
              <ShareIcon />
              <span>Share</span>
            </button>
          </div>
          {/* display ads here by google */}
          <DisplayAd style={{ margin: '16px 0', width: '100%' }} />
          {/* description */}
          <p className={style.desc}>{desc}&nbsp;
            <span style={{ color: "#e21220", cursor: "pointer", whiteSpace: "nowrap" }}
              onClick={() => setReadMore(!readmore)}>Read {`${readmore ? "Less" : "More.."}`}
            </span>
          </p>

          {isUpcoming
            ? <ImageSlider data={upComingData.slice(0, 4)} heading={"Upcoming Drama"} />
            : <>
              <h2>Episode List</h2>
              {drama.length > 1 ? <p className={style.ep_count}>{drama.length - 1} Episodes</p> : <p className={style.ep_count}>Trailer</p>}
              <div className={style.episodelist}>
                {drama.map((d, i) => (
                  <div className={style.edpisode_item}
                    key={d.epi_id}
                    onClick={() => playvideo(d, i)}>
                    <span className={d.epi_name === "Trailer" ? style.ep_trailer : style.ep_num}>{`${d.epi_name === "Trailer" ? "Trailer" : drama[0].epi_name === "Trailer" ? i : i + 1}`}</span>
                    {(() => {
                      // Show lock only if episode is locked and NOT unlocked via coins and user is not premium
                      if (
                        d.unlock_count === "1" &&
                        !isUnlockedViaCoinStore(d.epi_id) &&
                        (!user || user?.premium_data?.premium !== "1")
                      ) {
                        return <span className={style.lock}><LockIcon /></span>;
                      }
                      return null;
                    })()}
                    <MusicBar d={d} active={active} isplaying={isplaying} />
                  </div>
                ))}
              </div>
            </>}
          <ImageSlider data={recomendedData} heading={"Recommended Dramas"} />
        </div>
      </div>


      {/* modal  */}
      {modal && <UnlockModal style={style} close={() => setModal(false)} />}
    </>
  );
}

export default Player;

